import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import LandingNavBar from "../../components/common/landingNavBar";
import GameCard from "../../components/common/gameCard";
import UserInfo from "../../components/layouts/userInfo";
import GameDialog from "../../components/common/gameDialog";
import { RiSearchLine } from "react-icons/ri";
import { landingDatas } from "../../components/layouts/landingComponents/landingDatas";
import PlayButton from "../../components/common/playButton";
import { getGamesApi, getVendorsApi } from "../../services/authApi";
import { t, getLang, getGameTitle } from "../../lang";

export default function Favorite({ showLogin, isAuthenticated, userData, userBalance, onSignOut }){
  const observer = useRef(null);
  const navigate = useNavigate() ;
  const [isLoading, setIsLoading] = useState(false);
  const [vendorsData, setVendorsData] = useState([]);
  const [gamesData, setGamesData] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadMore, setIsLoadMore] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [showGameDialog, setShowGameDialog] = useState(false);
  const [isInitFromUrl, setIsInitFromUrl] = useState(true);
  const [isUrlSynced, setIsUrlSynced] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [showVendors, setShowVendors] = useState(false);
  const [lang, setLangState] = useState(getLang());

  useEffect(() => {
      const handler = () => setLangState(getLang());
      window.addEventListener("langChanged", handler);
      return () => window.removeEventListener("langChanged", handler);
  }, []);

  const init = {
      search: "",
      page: 1,
      perPage: 48,
      vendor: null, 
  };
    const [filter, setFilter] = useState(init);
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        const page = 1;
        const search = searchParams.get("search") || "";
        const vendor = searchParams.get("vendor");
        setFilter((prev) => ({
            ...prev,
            page,
            search,
            vendor,
        }));
        setSearchInput(search);
        setIsUrlSynced(true);
    }, []);
    useEffect(() => {
        const params = {};

        if (filter.page > 1) params.page = filter.page;
        if (filter.search) params.search = filter.search;
        if (filter.vendor) params.vendor = filter.vendor;
        setSearchParams(params, { replace: true });
    }, [filter.page, filter.search, filter.vendor]);
 
    useEffect(() => {
        const timer = setTimeout(() => {
            setFilter((prev) => ({
                ...prev,
                search: searchInput,
                page: 1,
            }));
        }, 500);

        return () => clearTimeout(timer);
    }, [searchInput]);

    const handleGameCardClick = (game) => {
        if (!isAuthenticated) {
        showLogin();
        } else {
            const isDesktop = window.innerWidth >= 1024; // Example breakpoint for desktop
            if (isDesktop) {
                const url = `/game?gameCode=${game.gameCode}&vendorCode=${game.vendorCode}`;
                const windowName = `game_${game.gameCode}_${Date.now()}`;

                window.open(
                url,
                windowName,
                "width=1280,height=900,toolbar=no,menubar=no,location=no,status=no"
                );
            } else {
                setSelectedGame(game);
                setShowGameDialog(true);
            }
        }
    };

    const handleLoadMore = () => {
        if (isLoadingMore || !isLoadMore) return;

        setIsLoadingMore(true);

        setFilter((prev) => ({
            ...prev,
            page: prev.page + 1,
        }));
    };

    const fetchGames = async (page, reset = false) => {
        if (!isAuthenticated) {
            setGamesData([])
            return;
        }

        try {
            if(reset){
                setIsLoading(true);
            }

            const userId = JSON.parse(localStorage.getItem("userData"))?.id;

            const res = await getGamesApi({
                userId,
                page,
                perPage: filter.perPage,
                search: filter.search,
                type: null,
                vendor: filter.vendor,
                pageType: "favorite"
            });

            const games = res?.data?.data || [];
        
            setGamesData((prev) =>
                reset ? games : [...prev, ...games]
            );
          
            
            setIsLoadMore(res.data.has_more);
        } finally {
            if(reset){
                setIsLoading(false);
            }
            setIsLoadingMore(false);
        }
    };

    useEffect(() => {
         if (!isUrlSynced || !isInitFromUrl) return;
            console.log("filter page:", filter.page);

            (async () => {
                setGamesData([]);
                for (let p = 1; p <= filter.page; p++) {
                    await fetchGames(p, p === 1);
                }

                setIsInitFromUrl(false);
            })();
       
    },  [isUrlSynced]);

    useEffect(() => {
        if (isInitFromUrl) return; 

        fetchGames(filter.page, filter.page === 1);
    }, [filter.page, filter.search, filter.vendor, isAuthenticated]);

    // Fetch vendors and games on component mount
    useEffect(() => {
        if (!isAuthenticated) {
            setIsInitialLoading(false);
            setShowVendors(false);
            return;
        }

        const fetchData = async () => {
            try {
                setIsInitialLoading(true);
                const userData = JSON.parse(localStorage.getItem('userData'));
                const userId = userData?.id || null; 
                const vendorsResponse = await getVendorsApi( { userId , type: null });
                 
                
                // Handle response - check if data is directly an array or wrapped in an object
                const vendors = Array.isArray(vendorsResponse.data) 
                    ? vendorsResponse.data 
                    : (vendorsResponse.data?.data || vendorsResponse.data?.vendors || [])
                
                setVendorsData(Array.isArray(vendors) ? vendors : []);
            } catch (error) {
                console.error("Error fetching data:", error);
                setVendorsData([]);
            } finally {
                setIsInitialLoading(false);
            }
        };
        
        fetchData();
    }, []);

    const handleVendorClick = (vendorCode) => {
       setGamesData([]);
        setFilter((prev) => ({
            ...prev,
            vendor:
            prev.vendor === vendorCode ? null : vendorCode,
            page: 1,
        }));
    }

    const lastElementRef = useCallback(
        (node) => {
            if (isLoadingMore) return;

            if (observer.current) observer.current.disconnect();

            observer.current = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && isLoadMore) {
                        handleLoadMore();
                    }
                },
                {
                    rootMargin: "200px",
                }
            );

            if (node) observer.current.observe(node);
        },
        [isLoadingMore, isLoadMore]
    );

    const location = useLocation();
    const navigateOther = (link)  => {
        const path = `/${link}`;
        if (location.pathname === path) return;
        navigate(path);
    };
    return(
        <>
            <div className='Landing'>
                <div className='landing-body' style={{paddingBottom: '5%', minHeight: '5%' }}>
                    {isAuthenticated && (
                        <>
                            <div className='landing-search-div'>
                                <input className='landing-search' placeholder={t('common.search_games')}
                                       value={searchInput}
                                       onChange={(e) => setSearchInput(e.target.value)}
                                />
                                <RiSearchLine />
                            </div>
                            {/*<div className='landing-filter' onClick={() => setShowVendors(prev => !prev)}>*/}
                            {/*    <span> { t("menu.all_providers") } </span>*/}
                            {/*    <img src='/img/icons/Vector.svg' alt='menu' />*/}
                            {/*</div>*/}

                            {/* <div className='landing-fields'>
                                {
                                    landingDatas.map( ( item, index ) => (
                                        <div className='landing-field' key={ index }>
                                            <img className='landing-image-back' src={ `/img/first-page/back-${ index + 1 }.png` } alt='background' />
                                            <img className={ `landing-image-girl fix-img${index}` }
                                                src={ `/img/first-page/g${ index + 1 }.png` } alt='girl' />
                                            <div className='casino_btn landing_btn'>
                                                <PlayButton title = { <span> { item.name } </span>} type = { 'grey' } action={() => navigateOther(item.link)} />
                                            </div>
                                        </div>
                                    ) )
                                }
                            </div> */}
                        </>
                    )}
                </div>
            </div>

        {/* {!isAuthenticated && (
            <div className='landing-img'>
                <img src="/img/favorite.png" alt="landingImage" />
            </div> 
        )} */}
            <div className="landing-img-mobile">
                <img src="/img/favorite.png" alt="favorite" />
                {/* Mobile Search */}
                <div className="mobile-search-wrapper">
                    <div className="mobile-search">
                        <RiSearchLine className="mobile-search-icon" />
                        <input
                            placeholder={t('common.search_games')}
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                        />
                    </div>
                </div>
            </div>
            {/* {!isAuthenticated && (
                <div className='mobile-login-button' onClick={showLogin}>
                    Login
                </div>
            )} */}
            {isAuthenticated && (
                <div className="mobile-user-info-container">
                    <UserInfo userData={userData} userBalance={userBalance} />
                </div>
            )}

            <LandingNavBar />
            
            <div className="page-body">
                 <div className="newgame-title">
                    <span className="page-title">{t('menu.favorite_game')}</span>
                    <span className="page-title-mobile">{t('menu.favorite_game')}</span>
                    {/*<span>{t('menu.supplier')}</span>*/}
                </div>
                {showVendors && (
                    <div className="newgame-items">
                        {Array.isArray(vendorsData) && vendorsData.map( ( vendor, index ) => (
                            <React.Fragment key={vendor.vendorCode}>
                            {vendor.logo? (
                                    <div 
                                    className={`newgame-item-logo ${filter.vendor === vendor.vendorCode ? 'active' : ''}`}
                                    key={index}
                                    onClick={() => handleVendorClick(vendor.vendorCode)}
                                    >
                                    <div className="vendor-logo-wrapper">
                                        <img
                                        src={`${process.env.REACT_APP_API_URL}/${vendor.logo}`}
                                        alt={vendor.vendorCode}
                                        className="vendor-logo"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = `${process.env.REACT_APP_API_URL}/images/logo.png`;
                                        }}
                                        />
                                    </div>
                                    </div>
                                ):(
                                    <div 
                                        className={`newgame-item ${filter.vendor === vendor.vendorCode ? 'active' : ''}`}
                                        key={ index }
                                        onClick={() => handleVendorClick(vendor.vendorCode)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <span>{ vendor.vendorCode }</span> 
                                        <span>{ vendor.vendorType }</span>
                                    </div>
                                
                                )
                            }
                            </React.Fragment>
                        ) )}
                    </div>
                )}
                {/* <div className="newgame-items-mobile">
                    <div className="newgame-items-mobile-view">
                    {Array.isArray(vendorsData) && vendorsData.map( ( vendor, index ) => (
                        <div
                            className={`newgame-item-mobile ${filter.vendor === vendor.vendorCode ? 'active' : ''}`}
                            key={ index }
                            onClick={() => handleVendorClick(vendor.vendorCode)}
                            style={{ cursor: 'pointer' }}
                        >
                             {vendor.logo? (
                                <div
                                className={`newgame-item-logo ${filter.vendor === vendor.vendorCode ? 'active' : ''}`}
                                key={index}
                                >
                                <div className="vendor-logo-wrapper">
                                    <img
                                    src={`${process.env.REACT_APP_API_URL}/${vendor.logo}`}
                                    alt={vendor.vendorCode}
                                    className="vendor-logo"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = `${process.env.REACT_APP_API_URL}/images/logo.png`;
                                    }}
                                    />
                                </div>
                                </div>
                             ) : (
                                <>
                                    <span>{ vendor.vendorCode }</span>
                                    <span>{ vendor.vendorType }</span>
                                </>
                             )}
                        </div>
                    ))}
                    </div>
                </div> */}
                {/* <div className="newgames-cards"> */}
                    {/* <div className="newgame-card">
                        <img src="" alt="card-image"/>
                        <span>starlight princess</span>
                    </div> */}
                    {/* <img src="/img/image 3.png" alt="gamecards"/>
                </div> */}
                <div className="new-game-cards">
                    {isInitialLoading || isLoading ? (
                        <div className="loading-container">
                            <div className="loading-spinner"></div>
                            <span className="loading-text">{t("common.loading")}</span>
                        </div>
                    ) : gamesData.length === 0 ? (
                        <div className="no-games-container">
                            <div className="no-games-icon">🎮</div>
                            <span className="no-games-text">{t("message.no_games_found")}</span>
                        </div>
                    ) : (
                        <>
                            {gamesData.map( ( game, index ) => (
                                <GameCard 
                                    title={ getGameTitle(game, lang) }
                                    image={ game.thumbnail }
                                    recent={game.recent}
                                    recommended={game.recommended}
                                    isAvailable={game.is_available}
                                    key={ index }
                                    index={index}
                                    onClick={() => handleGameCardClick(game)}
                                />
                            ) )}
                            {isLoadMore && (
                                <div ref={lastElementRef} className="load-more-container">
                                    {isLoadingMore && (
                                        <div className="load-more-loading">
                                            <div className="loading-spinner"></div>
                                            <span className="loading-text">{t("common.loading")}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            <GameDialog 
                isOpen={showGameDialog} 
                onClose={() => setShowGameDialog(false)} 
                gameData={selectedGame}
            />
        </>
    )
}